import { initializeTheme } from '@/hooks/use-appearance'
import PersistentLayout from '@/layouts/persistent-layout'
import { createInertiaApp, type ResolvedComponent } from '@inertiajs/react'
import { ReactNode, StrictMode } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'

const appName = import.meta.env.VITE_APP_NAME ?? 'picchat.ai'
const defaultTitle = 'Your image creation studio'

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  title: (title) =>
    title ? `${title} - ${appName}` : `${defaultTitle} - ${appName}`,

  // Disable progress bar
  //
  // see https://inertia-rails.dev/guide/progress-indicators
  // progress: false,

  resolve: (name) => {
    // const pages = import.meta.glob<ResolvedComponent>('../pages/**/*.tsx', {
    const pages = import.meta.glob<{ default: ResolvedComponent }>(
      '../pages/**/*.tsx',
      {
        eager: true,
      },
    )
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
    }

    // To use a default layout, import the Layout component
    // and use the following line.
    // see https://inertia-rails.dev/guide/pages#default-layouts
    //
    // page.default.layout ||= (page) => (<Layout>{page}</Layout>)
    page.default.layout ??= (page: ReactNode) => (
      <PersistentLayout>{page}</PersistentLayout>
    )

    return page
  },

  setup({ el, App, props }) {
    // when SSR
    if (el.hasChildNodes()) {
      hydrateRoot(el, <App {...props} />)
      return
    }
    createRoot(el).render(
      <StrictMode>
        <App {...props} />
      </StrictMode>,
    )
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: true,
    },
    future: {
      useScriptElementForInitialPage: true,
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById('app')) {
    throw error
  } else {
    console.error(
      'Missing root element.\n\n' +
        'If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n' +
        'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
    )
  }
})

// This will set light / dark mode on load...
initializeTheme()
